
import _ = require('underscore');
import { Set } from '~commonInfra/utils/Set';

import { siteUrlsSelector } from '../../../bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { Formatters,Messages } from '../../../codegen/Localize';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { IError } from '../../../httpClient/serverApi/types/IError';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { IIdItemInfo } from '../../../spacesPlatform/models/resourceInfoParts';
import { IUserInfo } from '../../users/models/IUserInfo';

// Notification helpers

const nameFor = (user: IUserInfo): string => {
  return Messages.userActionNameFormattedForToast({ DISPLAY_NAME: L10n.elideIfLong(user.displayName), USER_NAME: L10n.elideIfLong(user.username) });
};

// get name of a successful item
// returns empty string if no items successful
const getSuccessfulName = (users: Set<IUserInfo>, result: IResult): string => {
  const successfulItem = getSuccessfulItem(users, result);
  return successfulItem ? nameFor(successfulItem) : '';
};

// get name of a failing item
// returns empty string if no items failed
const getFailingName = (users: Set<IUserInfo>, result: IResult): string => {
  const failingItem = getFailingItem(users, result);
  return failingItem ? nameFor(failingItem) : '';
};

// get a successful item, useful when there is only one item that is successful
// returns undefined if no items successful
const getSuccessfulItem = (users: Set<IUserInfo>, result: IResult): IUserInfo => {
  return users.find((item: IIdItemInfo) => {
    let matchingError: IError = undefined;
    if (result.errors) {
      matchingError = _.find(result.errors, (error: IError) => {
        return item.id === error.id;
      });
    }
    return !matchingError;
  });
};

// get a failing item, useful when there is only one item that is failing
// returns undefined if no items failed
const getFailingItem = (users: Set<IUserInfo>, result: IResult): IUserInfo => {
  return users.find((item: IIdItemInfo) => {
    let matchingError: IError = undefined;
    if (result.errors) {
      matchingError = _.find(result.errors, (error: IError) => {
        return item.id === error.id;
      });
    }
    return !!matchingError;
  });
};

// get the URL to the specified user's profile page
const getUrlForUser = (user: IUserInfo) => {
  if (user.username === 'guest' && user.domainName === 'local') {
    return null;
  } else {
    const siteUrls = siteUrlsSelector(getAppStore().getState());
    return siteUrls.userUrl(user.username, user.domainName);
  }
};

const userActionBulkOperationResultLocKeyDict: Record<number, (count: number) => string> = {
  1: (count: number) => Messages.userActionBulkOperationResult_1({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  10: (count: number) => Messages.userActionBulkOperationResult_10({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  11: (count: number) => Messages.userActionBulkOperationResult_11({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  12: (count: number) => Messages.userActionBulkOperationResult_12({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  13: (count: number) => Messages.userActionBulkOperationResult_13({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  14: (count: number) => Messages.userActionBulkOperationResult_14({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  15: (count: number) => Messages.userActionBulkOperationResult_15({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  20: (count: number) => Messages.userActionBulkOperationResult_20({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  21: (count: number) => Messages.userActionBulkOperationResult_21({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  30: (count: number) => Messages.userActionBulkOperationResult_30({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  31: (count: number) => Messages.userActionBulkOperationResult_31({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  32: (count: number) => Messages.userActionBulkOperationResult_32({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  40: (count: number) => Messages.userActionBulkOperationResult_40({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  41: (count: number) => Messages.userActionBulkOperationResult_41({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  42: (count: number) => Messages.userActionBulkOperationResult_42({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  50: (count: number) => Messages.userActionBulkOperationResult_50({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  51: (count: number) => Messages.userActionBulkOperationResult_51({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  52: () => Messages.userActionBulkOperationResult_52(),
  53: (count: number) => Messages.userActionBulkOperationResult_53({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  54: (count: number) => Messages.userActionBulkOperationResult_54({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  55: (count: number) => Messages.userActionBulkOperationResult_55({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  56: (count: number) => Messages.userActionBulkOperationResult_56({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  57: (count: number) => Messages.userActionBulkOperationResult_57({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  58: (count: number) => Messages.userActionBulkOperationResult_58({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  59: (count: number) => Messages.userActionBulkOperationResult_59({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  60: (count: number) => Messages.userActionBulkOperationResult_60({COUNT: count, COUNT_LOC: Formatters.formatCount(count)}),
  61: () => Messages.userActionBulkOperationResult_61(),
  62: () => Messages.userActionBulkOperationResult_62(),
  63: (count: number) => Messages.userActionBulkOperationResult_63({COUNT_LOC: Formatters.formatCount(count)}),
};

const userActionBulkOperationErrorResultLocKeyDict: Record<number, (siteRole?: string, poolname?: string) => string> = {
  10004: Messages.userActionBulkOperationErrorResult_10004,
  10005: Messages.userActionBulkOperationErrorResult_10005,
  10009: Messages.userActionBulkOperationErrorResult_10009,
  10010: Messages.userActionBulkOperationErrorResult_10010,
  10011: Messages.userActionBulkOperationErrorResult_10011,
  10012: Messages.userActionBulkOperationErrorResult_10012,
  10015: Messages.userActionBulkOperationErrorResult_10015,
  10017: Messages.userActionBulkOperationErrorResult_10017,
  10018: Messages.userActionBulkOperationErrorResult_10018,
  10019: Messages.userActionBulkOperationErrorResult_10019,
  10020: Messages.userActionBulkOperationErrorResult_10020,
  10021: Messages.userActionBulkOperationErrorResult_10021,
  10023: Messages.userActionBulkOperationErrorResult_10023,
  10026: Messages.userActionBulkOperationErrorResult_10026,
  10027: Messages.userActionBulkOperationErrorResult_10027,
  10050: Messages.userActionBulkOperationErrorResult_10050,
  10051: Messages.userActionBulkOperationErrorResult_10051,
  10052: Messages.userActionBulkOperationErrorResult_10052,
  10053: Messages.userActionBulkOperationErrorResult_10053,
  10054: Messages.userActionBulkOperationErrorResult_10054,
  10055: Messages.userActionBulkOperationErrorResult_10055,
  10070: Messages.userActionBulkOperationErrorResult_10070,
  10071: Messages.userActionBulkOperationErrorResult_10071,
  10072: Messages.userActionBulkOperationErrorResult_10072,
  10073: Messages.userActionBulkOperationErrorResult_10073,
  10079: (siteRole: string) => Messages.errorUserCannotHaveSiteRoleLowerThan_highestMinimumLulGroupRole({ SITE_ROLE: siteRole}),
  10093: Messages.userActionBulkOperationErrorResult_10093,
  30: Messages.userActionBulkOperationErrorResult_30,
  157: (poolname: string) => Messages.userActionBulkOperationErrorResult_157({POOL_NAME: L10n.elideIfLong(poolname)}),
  55: Messages.userActionBulkOperationErrorResult_55
};

const userActionResultLocKeyDict: Record<number, (username: string, licenseTier?: string) => string> = {
  1: (username: string) => Messages.userActionResult_1({USER_NAME: L10n.elideIfLong(username)}),
  10: (username: string) => Messages.userActionResult_10({USER_NAME: L10n.elideIfLong(username)}),
  11: (username: string) => Messages.userActionResult_11({USER_NAME: L10n.elideIfLong(username)}),
  12: (username: string) => Messages.userActionResult_12({USER_NAME: L10n.elideIfLong(username)}),
  13: (username: string) => Messages.userActionResult_13({USER_NAME: L10n.elideIfLong(username)}),
  14: (username: string) => Messages.userActionResult_14({USER_NAME: L10n.elideIfLong(username)}),
  15: (username: string) => Messages.userActionResult_15({USER_NAME: L10n.elideIfLong(username)}),
  20: (username: string) => Messages.userActionResult_20({USER_NAME: L10n.elideIfLong(username)}),
  21: (username: string) => Messages.userActionResult_21({USER_NAME: L10n.elideIfLong(username)}),
  30: (username: string) => Messages.userActionResult_30({USER_NAME: L10n.elideIfLong(username)}),
  31: (username: string, licenseTier?: string) => Messages.userActionResult_31({USER_NAME: L10n.elideIfLong(username), LICENSE_TIER: L10n.elideIfLong(licenseTier)}),
  32: (username: string) => Messages.userActionResult_32({USER_NAME: L10n.elideIfLong(username)}),
  40: (username: string) => Messages.userActionResult_40({USER_NAME: L10n.elideIfLong(username)}),
  41: (username: string) => Messages.userActionResult_41({USER_NAME: L10n.elideIfLong(username)}),
  42: (username: string) => Messages.userActionResult_42({USER_NAME: L10n.elideIfLong(username)}),
  50: (username: string) => Messages.userActionResult_50({USER_NAME: L10n.elideIfLong(username)}),
  51: (username: string) => Messages.userActionResult_51({USER_NAME: L10n.elideIfLong(username)}),
  52: (username: string) => Messages.userActionResult_52({USER_NAME: L10n.elideIfLong(username)}),
  54: (username: string) => Messages.userActionResult_54({USER_NAME: L10n.elideIfLong(username)}),
  55: (username: string) => Messages.userActionResult_55({USER_NAME: L10n.elideIfLong(username)}),
  56: (username: string) => Messages.userActionResult_56({USER_NAME: L10n.elideIfLong(username)}),
  57: (username: string) => Messages.userActionResult_57({USER_NAME: L10n.elideIfLong(username)}),
  58: (username: string) => Messages.userActionResult_58({USER_NAME: L10n.elideIfLong(username)}),
  59: (username: string) => Messages.userActionResult_59({USER_NAME: L10n.elideIfLong(username)}),
  60: (username: string) => Messages.userActionResult_60({USER_NAME: L10n.elideIfLong(username)}),
  61: (username: string) => Messages.userActionResult_61(),
  62: (username: string) => Messages.userActionResult_62({USER_NAME: L10n.elideIfLong(username)}),
  63: (username: string) => Messages.userActionResult_63({USER_NAME: L10n.elideIfLong(username)}),
};

export const NotificationHelper = {
  nameFor, getSuccessfulName, getFailingName, getSuccessfulItem, getFailingItem, getUrlForUser,
  userActionBulkOperationResultLocKeyDict, userActionBulkOperationErrorResultLocKeyDict, userActionResultLocKeyDict
};
